import React, { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import moment from 'moment';
import { makeStyles, useTheme } from '@mui/styles';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

//import { getInitials } from 'helpers';
import { NavLink } from 'react-router-dom';
import { updateSelectedRestaurantIds } from 'store/actions/restaurant'
import { useDispatch, useSelector } from 'react-redux';
import { checkAdmin, checkMenuAdmin } from 'util/userUtils';

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: 0
  },
  inner: {
    minWidth: 1200
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  textLink: {
		textDecoration: 'underline',
	},
  // --- Compact (phone / folded foldable / small tablet) card list ---
  cardListHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 1.5, 0, 1.5)
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5)
  },
  listCard: {
    padding: theme.spacing(1.5)
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5)
  },
  cardTitle: {
    minWidth: 0,
    flex: 1,
    paddingTop: theme.spacing(1)
  },
  cardChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  },
  cardFields: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap'
  },
  cardValue: {
    fontSize: '0.85rem',
    overflowWrap: 'anywhere'
  },
  idWithCopy: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  pagination: {
    width: '100%',
    '& .MuiTablePagination-toolbar': {
      flexWrap: 'wrap',
      justifyContent: 'flex-end'
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiTablePagination-toolbar': {
        paddingLeft: theme.spacing(1)
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: '0.75rem',
        margin: 0
      },
      '& .MuiTablePagination-actions': {
        marginLeft: theme.spacing(1)
      }
    }
  }
}));

// Copies a restaurant id; the tooltip confirms the copy (or reports a blocked
// clipboard) instead of a global snackbar. The result is forced open so a tap
// on a touch screen, which never hovers, still gets feedback.
const CopyIdButton = ({ id }) => {
  const [status, setStatus] = useState('idle');
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (status === 'idle') {
      return undefined;
    }
    const timer = setTimeout(() => setStatus('idle'), 1500);
    return () => clearTimeout(timer);
  }, [status]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setStatus('copied');
    } catch (err) {
      console.error('Failed to copy restaurant id:', err);
      setStatus('failed');
    }
  };

  const title = status === 'copied' ? 'Copied!' : status === 'failed' ? 'Copy failed' : 'Copy restaurant id';

  return (
    <Tooltip
      title={title}
      open={hovered || status !== 'idle'}
      onOpen={() => setHovered(true)}
      onClose={() => setHovered(false)}
    >
      <IconButton size="small" onClick={handleCopy} aria-label={`copy restaurant id ${id}`}>
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
};

CopyIdButton.propTypes = {
  id: PropTypes.string.isRequired
};

const RestaurantsTable = props => {
  const { className, restaurants, statusFilter, paymentFilter, deliveryProviderFilter, terminalFilter, kdsFilter, ...rest } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();

  // Below `md` a table this wide (8 columns, 11 for admins) cannot be read
  // without horizontal scrolling, so phones, folded foldables and portrait
  // tablets get a stacked card list.
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const updateRestaurantIds = (ids) => dispatch(updateSelectedRestaurantIds(ids));
  const restaurantFilter = useSelector((state) => state.restaurant.filter);
  const user = useSelector((state) => state.account.user);
  // The surcharge column exposes commercial terms and stays admin-only.
  // Terminal and KDS are operational; admin and menuAdmin both receive the
  // fields they read in the list response (other roles get a filtered shape
  // without them, so the columns hide rather than mislead).
  const isAdmin = checkAdmin(user);
  const isMenuAdmin = checkMenuAdmin(user);

  const handleSelectAll = event => {
 
    let selectedRestaurants;

    if (event.target.checked) {
      selectedRestaurants = (restaurants || []).map(restaurant => restaurant.urlIdentifier);
    } else {
      selectedRestaurants = [];
    }

    setSelectedRestaurants(selectedRestaurants);
  };

  const handleSelectOne = (event, urlIdentifier) => {
    const selectedIndex = selectedRestaurants.indexOf(urlIdentifier);
    let newSelectedRestaurants = [];

    if (selectedIndex === -1) {
      newSelectedRestaurants = newSelectedRestaurants.concat(selectedRestaurants, urlIdentifier);
    } else if (selectedIndex === 0) {
      newSelectedRestaurants = newSelectedRestaurants.concat(selectedRestaurants.slice(1));
    } else if (selectedIndex === selectedRestaurants.length - 1) {
      newSelectedRestaurants = newSelectedRestaurants.concat(selectedRestaurants.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedRestaurants = newSelectedRestaurants.concat(
        selectedRestaurants.slice(0, selectedIndex),
        selectedRestaurants.slice(selectedIndex + 1)
      );
    }

    setSelectedRestaurants(newSelectedRestaurants);
    updateRestaurantIds(newSelectedRestaurants);
  };

  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const paymentChipColors = {
    Stripe:          { bg: '#635bff', text: '#fff' },
    Adyen:           { bg: '#0abf53', text: '#fff' },
    Till:            { bg: '#ff9800', text: '#fff' },
    'China Payments': { bg: '#e53935', text: '#fff' },
    HuePay:          { bg: '#1565c0', text: '#fff' },
  };

  const getPaymentTypes = (restaurant) => {
    const types = [];
    if (restaurant.stripeAccountId) types.push('Stripe');
    if (restaurant.adyenPaymentSettings) types.push('Adyen');
    if (restaurant.onlineSettings?.isTillPaymentEnabled) types.push('Till');
    if (restaurant.chinaPaymentsMerchantCode) types.push('China Payments');
    if (restaurant.huePayMerchantNumber) types.push('HuePay');
    return types;
  };

  const hasOnlinePayment = (restaurant) => {
    return getPaymentTypes(restaurant).length > 0;
  };

  const terminalChipColors = {
    Adyen: { bg: '#0abf53', text: '#fff' },
    ANZ:   { bg: '#007dba', text: '#fff' },
    Other: { bg: '#9e9e9e', text: '#fff' },
  };

  // An admin-recorded terminalProvider wins. Otherwise derive it from what the
  // POS itself reports: enabling the ANZ terminal in POS settings pushes
  // {"IsANZTerminalEnabled": true} into the restaurant's obAppSettings blob
  // (updateRestaurantSettings type "obAppSettings"), so that flag is the
  // terminal-specific signal - a venue can take Adyen online yet run an ANZ
  // terminal at the counter. Adyen terminals need adyenPaymentSettings (the POS
  // fetches them via /terminals, which 404s without it). Anything else is an
  // unintegrated standalone terminal.
  const getTerminalProvider = (restaurant) => {
    if (restaurant.terminalProvider) {
      return restaurant.terminalProvider;
    }
    if (restaurant.obAppSettings?.IsANZTerminalEnabled) {
      return 'ANZ';
    }
    return restaurant.adyenPaymentSettings ? 'Adyen' : 'Other';
  };

  const renderTerminalChip = (restaurant) => {
    const provider = getTerminalProvider(restaurant);
    return (
      <Chip
        label={provider}
        size="small"
        sx={{
          backgroundColor: terminalChipColors[provider]?.bg || terminalChipColors.Other.bg,
          color: terminalChipColors[provider]?.text || terminalChipColors.Other.text,
          fontWeight: 600,
          fontSize: '0.7rem',
        }}
      />
    );
  };

  // KDS is on unless explicitly disabled in the restaurant's POS settings.
  const renderKdsChip = (restaurant) => (
    <Chip
      label={restaurant.isKDSDisabled ? 'Off' : 'On'}
      size="small"
      sx={{
        backgroundColor: restaurant.isKDSDisabled ? '#9e9e9e' : '#4caf50',
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );

  const formatInPersonSurcharge = (restaurant) => {
    const rate = restaurant.inPersonPaymentSurchargeRate;
    if (rate === undefined || rate === null || rate === '' || isNaN(Number(rate))) {
      return '—';
    }
    return `${Number(rate)}%`;
  };

  const deliveryProviderChipColors = {
    UberEats: { bg: '#06c167', text: '#fff' },
  };

  const getDeliveryProviders = (restaurant) => {
    const providers = [];
    if (restaurant.uberStoreName && restaurant.uberStoreName.trim() !== '') {
      providers.push('UberEats');
    }
    return providers;
  };

  const hasDeliveryProvider = (restaurant) => {
    return getDeliveryProviders(restaurant).length > 0;
  };

  const filteredRestaurants = useMemo(() => {
    if (!restaurants || !Array.isArray(restaurants)) {
      return [];
    }

    let filtered = restaurants;

    if (restaurantFilter && restaurantFilter.trim() !== '') {
      const lowerCaseFilter = restaurantFilter.toLowerCase();
      filtered = filtered.filter(restaurant =>
        (restaurant.name && restaurant.name.toLowerCase().includes(lowerCaseFilter))
        || (restaurant.altName && restaurant.altName.toLowerCase().includes(lowerCaseFilter))
        || (restaurant.restaurantAdmin && restaurant.restaurantAdmin.toLowerCase().includes(lowerCaseFilter))
        || (restaurant.description && restaurant.description.toLowerCase().includes(lowerCaseFilter))
        || (restaurant.urlIdentifier && restaurant.urlIdentifier.toLowerCase().includes(lowerCaseFilter))
        || (restaurant.address && restaurant.address.toLowerCase().includes(lowerCaseFilter))
      );
    }

    if (statusFilter === 'open') {
      filtered = filtered.filter(r => !r.isClosed);
    } else if (statusFilter === 'closed') {
      filtered = filtered.filter(r => r.isClosed);
    }

    if (paymentFilter === 'hasPayment') {
      filtered = filtered.filter(r => hasOnlinePayment(r));
    } else if (paymentFilter === 'noPayment') {
      filtered = filtered.filter(r => !hasOnlinePayment(r));
    }

    if (deliveryProviderFilter === 'uberEats') {
      filtered = filtered.filter(r => getDeliveryProviders(r).includes('UberEats'));
    } else if (deliveryProviderFilter === 'none') {
      filtered = filtered.filter(r => !hasDeliveryProvider(r));
    }

    // Filter values match the provider names the Terminal column shows.
    if (terminalFilter && terminalFilter !== 'all') {
      filtered = filtered.filter(r => getTerminalProvider(r) === terminalFilter);
    }

    if (kdsFilter === 'on') {
      filtered = filtered.filter(r => !r.isKDSDisabled);
    } else if (kdsFilter === 'off') {
      filtered = filtered.filter(r => r.isKDSDisabled);
    }

    return filtered;
  }, [restaurants, restaurantFilter, statusFilter, paymentFilter, deliveryProviderFilter, terminalFilter, kdsFilter]);

  const renderDeliveryProviderChips = (restaurant) => {
    const providers = getDeliveryProviders(restaurant);
    if (providers.length === 0) {
      return <Typography variant="body2" color="text.secondary">None</Typography>;
    }
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {providers.map((provider) => (
          <Chip
            key={provider}
            label={provider}
            size="small"
            sx={{
              backgroundColor: deliveryProviderChipColors[provider]?.bg,
              color: deliveryProviderChipColors[provider]?.text,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        ))}
      </div>
    );
  };

  const renderPaymentChips = (restaurant) => {
    const types = getPaymentTypes(restaurant);
    if (types.length === 0) {
      return <Typography variant="body2" color="text.secondary">None</Typography>;
    }
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {types.map((type) => (
          <Chip
            key={type}
            label={type}
            size="small"
            sx={{
              backgroundColor: paymentChipColors[type]?.bg,
              color: paymentChipColors[type]?.text,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        ))}
      </div>
    );
  };




  // Firestore timestamps are occasionally missing on legacy records.
  const formatTimestamp = (timestamp, format) => {
    if (!timestamp || !timestamp._seconds) {
      return '';
    }
    return moment(new Date(timestamp._seconds * 1000)).format(format);
  };

  const formatUpdated = (restaurant) => {
    const updated = formatTimestamp(restaurant.updateAt, 'DD/MM/YYYY HH:mm');
    return updated && restaurant.updateBy
      ? `${updated} by ${restaurant.updateBy}`
      : updated;
  };

  const restaurantLink = (restaurant) =>
    restaurant.type === 'restaurantGroup'
      ? `/restaurantGroup/${restaurant.urlIdentifier}`
      : `/restaurant/${restaurant.urlIdentifier}`;

  // One slice, shared by both layouts, so page state survives a resize.
  const pagedRestaurants = filteredRestaurants.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderId = (restaurant) => (
    <span className={classes.idWithCopy}>
      {restaurant.urlIdentifier}
      <CopyIdButton id={restaurant.urlIdentifier} />
    </span>
  );

  const CardField = ({ label, value }) => (
    <>
      <Typography component="dt" className={classes.cardLabel}>{label}</Typography>
      <Typography component="dd" className={classes.cardValue}>{value || '—'}</Typography>
    </>
  );

  CardField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.node
  };

  const renderCards = () => (
    <>
      <div className={classes.cardListHeader}>
        <Typography variant="body2" color="textSecondary">
          {filteredRestaurants.length} restaurant{filteredRestaurants.length === 1 ? '' : 's'}
        </Typography>
      </div>
      <div className={classes.cardList}>
        {pagedRestaurants.length === 0 ? (
          <Typography className={classes.emptyState} color="textSecondary">
            No restaurants to show.
          </Typography>
        ) : (
          pagedRestaurants.map(restaurant => (
            <Paper
              key={restaurant.urlIdentifier}
              variant="outlined"
              className={classes.listCard}
              data-testid="restaurant-card"
            >
              <div className={classes.cardHeader}>
                <Checkbox
                  checked={selectedRestaurants.indexOf(restaurant.urlIdentifier) !== -1}
                  color="primary"
                  onChange={event => handleSelectOne(event, restaurant.urlIdentifier)}
                  size="small"
                  inputProps={{ 'aria-label': `select ${restaurant.name}` }}
                />
                <div className={classes.cardTitle}>
                  <NavLink to={restaurantLink(restaurant)}>
                    <div className={classes.textLink}>
                      <Typography variant="body1">{restaurant.name}</Typography>
                    </div>
                  </NavLink>
                  {restaurant.altName && (
                    <Typography variant="body2" color="text.secondary">
                      {restaurant.altName}
                    </Typography>
                  )}
                </div>
                <Chip
                  label={restaurant.isClosed ? 'Closed' : 'Open'}
                  size="small"
                  sx={{
                    backgroundColor: restaurant.isClosed ? '#ef5350' : '#4caf50',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    marginTop: '8px'
                  }}
                />
              </div>

              <dl className={classes.cardFields}>
                <CardField label="Id" value={renderId(restaurant)} />
                <CardField label="Address" value={restaurant.address} />
                <CardField label="Payment" value={renderPaymentChips(restaurant)} />
                {isAdmin && (
                  <CardField
                    label="In-person surcharge"
                    value={formatInPersonSurcharge(restaurant)}
                  />
                )}
                {isMenuAdmin && (
                  <CardField label="Terminal" value={renderTerminalChip(restaurant)} />
                )}
                {isMenuAdmin && (
                  <CardField label="KDS" value={renderKdsChip(restaurant)} />
                )}
                <CardField label="Delivery" value={renderDeliveryProviderChips(restaurant)} />
                <CardField
                  label="Created"
                  value={formatTimestamp(restaurant.createdAt, 'DD/MM/YYYY')}
                />
                <CardField
                  label="Updated"
                  value={formatUpdated(restaurant)}
                />
              </dl>
            </Paper>
          ))
        )}
      </div>
    </>
  );

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <CardContent className={classes.content}>
        {isCompact ? renderCards() : (
          <TableContainer component={Paper}>
            <Table className={classes.inner}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {/* <Checkbox
                      checked={selectedRestaurants.length === restaurants.length}
                      color="primary"
                      indeterminate={
                        selectedRestaurants.length > 0 &&
                        selectedRestaurants.length < restaurants.length
                      }
                      onChange={handleSelectAll}
                    /> */}
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Restaurant Id</TableCell>
                  <TableCell>Online Payment</TableCell>
                  {isAdmin && <TableCell>In-Person Surcharge</TableCell>}
                  {isMenuAdmin && <TableCell>Terminal</TableCell>}
                  {isMenuAdmin && <TableCell>KDS</TableCell>}
                  <TableCell>Address</TableCell>
                  <TableCell>Delivery Service Providers</TableCell>
                  <TableCell>Created / Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRestaurants.map(restaurant => (
                  <TableRow
                    className={classes.tableRow}
                    hover
                    key={restaurant.urlIdentifier}
                    selected={selectedRestaurants.indexOf(restaurant.urlIdentifier) !== -1}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRestaurants.indexOf(restaurant.urlIdentifier) !== -1}
                        color="primary"
                        onChange={event => handleSelectOne(event, restaurant.urlIdentifier)}
                        value="true"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={restaurant.isClosed ? 'Closed' : 'Open'}
                        size="small"
                        sx={{
                          backgroundColor: restaurant.isClosed ? '#ef5350' : '#4caf50',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell >
                      <NavLink to={restaurantLink(restaurant)}>
                        <div className={classes.textLink}>
                          <Typography variant="body1">{restaurant.name}</Typography>
                        </div>
                      </NavLink>
                      {restaurant.altName && (
                        <Typography variant="body2" color="text.secondary">
                          {restaurant.altName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{renderId(restaurant)}</TableCell>
                    <TableCell>{renderPaymentChips(restaurant)}</TableCell>
                    {isAdmin && (
                      <TableCell>{formatInPersonSurcharge(restaurant)}</TableCell>
                    )}
                    {isMenuAdmin && (
                      <TableCell>{renderTerminalChip(restaurant)}</TableCell>
                    )}
                    {isMenuAdmin && <TableCell>{renderKdsChip(restaurant)}</TableCell>}
                    <TableCell>
                      {restaurant.address}
                    </TableCell>
                    <TableCell>{renderDeliveryProviderChips(restaurant)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Created: {formatTimestamp(restaurant.createdAt, 'DD/MM/YYYY')}
                      </Typography>
                      {restaurant.updateAt && restaurant.updateAt._seconds && (
                        <Typography variant="body2" color="text.secondary">
                          Updated: {formatUpdated(restaurant)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          className={classes.pagination}
          component="div"
          count={filteredRestaurants.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage={isCompact ? 'Rows' : 'Rows per page:'}
        />
      </CardActions>
    </Card>
  );
};

RestaurantsTable.propTypes = {
  className: PropTypes.string,
  restaurants: PropTypes.array.isRequired
};

export default RestaurantsTable;
